import {createContext, useCallback, useContext, useEffect, useState, ReactNode} from 'react'

/** An image to preview — either a bare URL or a URL with a caption title. */
export type LightboxItem = string | {src: string; title?: string}

interface NormItem {
  src: string
  title?: string
}

interface LightboxState {
  items: NormItem[]
  index: number
}

interface LightboxCtx {
  /** Open the preview at `items[index]`, with prev/next across the set. */
  open: (items: LightboxItem[], index?: number) => void
}

const Ctx = createContext<LightboxCtx>({open: () => {}})

export const useLightbox = () => useContext(Ctx)

const normalize = (items: LightboxItem[]): NormItem[] =>
  items.map((it) => (typeof it === 'string' ? {src: it} : it))

/**
 * Full-screen image preview for the public site. Wrap a subtree in
 * <LightboxProvider> and call `open(images, index)` from any click handler.
 * Supports backdrop-click + Esc to close and ←/→ (or on-screen arrows) to move.
 */
export const LightboxProvider = ({children}: {children: ReactNode}) => {
  const [state, setState] = useState<LightboxState | null>(null)

  const open = useCallback((items: LightboxItem[], index = 0) => {
    const norm = normalize(items)
    if (norm.length) setState({items: norm, index})
  }, [])
  const close = useCallback(() => setState(null), [])
  const go = useCallback(
    (dir: number) =>
      setState((s) => (s ? {...s, index: (s.index + dir + s.items.length) % s.items.length} : s)),
    [],
  )

  useEffect(() => {
    if (!state) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
      else if (e.key === 'ArrowRight') go(1)
      else if (e.key === 'ArrowLeft') go(-1)
    }
    window.addEventListener('keydown', onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [state, close, go])

  return (
    <Ctx.Provider value={{open}}>
      {children}
      {state && (
        <div
          onClick={close}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 2000,
            background: 'rgba(8,10,14,0.92)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
          }}
        >
          {/* Close */}
          <button onClick={close} aria-label='Close' style={btn({top: 18, right: 20})}>
            ✕
          </button>

          {/* Counter */}
          {state.items.length > 1 && (
            <div style={{position: 'absolute', top: 24, left: 0, right: 0, textAlign: 'center', color: 'rgba(255,255,255,0.85)', fontSize: 14, fontWeight: 600, letterSpacing: '0.05em'}}>
              {state.index + 1} / {state.items.length}
            </div>
          )}

          {/* Prev / Next */}
          {state.items.length > 1 && (
            <>
              <button onClick={(e) => {e.stopPropagation(); go(-1)}} aria-label='Previous' style={btn({left: 16, top: '50%', transform: 'translateY(-50%)'})}>
                ‹
              </button>
              <button onClick={(e) => {e.stopPropagation(); go(1)}} aria-label='Next' style={btn({right: 16, top: '50%', transform: 'translateY(-50%)'})}>
                ›
              </button>
            </>
          )}

          <figure onClick={(e) => e.stopPropagation()} style={{margin: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14}}>
            <img
              src={state.items[state.index].src}
              alt={state.items[state.index].title ?? ''}
              style={{maxWidth: '92vw', maxHeight: '82vh', objectFit: 'contain', borderRadius: 10, boxShadow: '0 20px 60px rgba(0,0,0,0.6)'}}
            />
            {state.items[state.index].title && (
              <figcaption style={{color: '#fff', fontSize: 16, fontWeight: 600, textAlign: 'center', textShadow: '0 1px 4px rgba(0,0,0,0.5)'}}>
                {state.items[state.index].title}
              </figcaption>
            )}
          </figure>
        </div>
      )}
    </Ctx.Provider>
  )
}

function btn(pos: React.CSSProperties): React.CSSProperties {
  return {
    position: 'absolute',
    ...pos,
    width: 44,
    height: 44,
    borderRadius: '50%',
    border: 'none',
    background: 'rgba(255,255,255,0.14)',
    color: '#fff',
    fontSize: 22,
    lineHeight: 1,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }
}
